import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export type InterestsByVibe = Record<string, string[]>;

const MAX_INTERESTS_PER_VIBE = 10;
const MAX_TOTAL_INTERESTS = 30;

const VIBE_KEYS = ['hungry', 'adventurous', 'must-sees', 'hidden-gems', 'popular', 'feeling-lucky', 'other'] as const;

export function useSavedInterests() {
  const [interests, setInterests] = useState<InterestsByVibe>({});
  const [useInterests, setUseInterests] = useState(false);
  const [loading, setLoading] = useState(false);

  const totalCount = Object.values(interests).reduce((sum, arr) => sum + arr.length, 0);
  const hasAny = totalCount > 0;

  const loadInterests = useCallback(async (userId: string) => {
    if (!supabase) return;
    setLoading(true);

    const { data } = await supabase
      .from('user_settings')
      .select('saved_interests, use_saved_interests')
      .eq('user_id', userId)
      .maybeSingle();

    if (data) {
      setInterests(data.saved_interests || {});
      setUseInterests(data.use_saved_interests || false);
    }
    setLoading(false);
  }, []);

  const saveToDb = useCallback(async (userId: string, newInterests: InterestsByVibe, newUseInterests?: boolean) => {
    if (!supabase) return;
    const update: Record<string, unknown> = { saved_interests: newInterests };
    if (newUseInterests !== undefined) update.use_saved_interests = newUseInterests;
    await supabase
      .from('user_settings')
      .update(update)
      .eq('user_id', userId);
  }, []);

  const addInterest = useCallback(async (userId: string, vibe: string, interest: string): Promise<boolean> => {
    const trimmed = interest.trim().toLowerCase();
    if (!trimmed) return false;

    const vibeKey = VIBE_KEYS.includes(vibe as typeof VIBE_KEYS[number]) ? vibe : 'other';
    const current = interests[vibeKey] || [];

    if (current.includes(trimmed)) return false;
    if (current.length >= MAX_INTERESTS_PER_VIBE) return false;
    if (totalCount >= MAX_TOTAL_INTERESTS) return false;

    const updated = { ...interests, [vibeKey]: [...current, trimmed] };
    setInterests(updated);
    await saveToDb(userId, updated);
    return true;
  }, [interests, totalCount, saveToDb]);

  const removeInterest = useCallback(async (userId: string, vibe: string, interest: string) => {
    const current = interests[vibe] || [];
    const updated = { ...interests, [vibe]: current.filter(i => i !== interest) };
    // Remove empty vibe keys
    if (updated[vibe]?.length === 0) delete updated[vibe];
    setInterests(updated);
    await saveToDb(userId, updated);
  }, [interests, saveToDb]);

  const toggleUseInterests = useCallback(async (userId: string, value: boolean) => {
    setUseInterests(value);
    await saveToDb(userId, interests, value);
  }, [interests, saveToDb]);

  /** Auto-save a custom vibe to the right category after a search */
  const autoSaveInterest = useCallback(async (
    userId: string,
    customVibe: string,
    pairedVibe: string | null,
    isPremium: boolean
  ): Promise<void> => {
    if (!isPremium) return;
    const trimmed = customVibe.trim().toLowerCase();
    if (!trimmed) return;

    // If paired with a standard vibe, save under that vibe. Otherwise 'other'.
    const vibeKey = pairedVibe && VIBE_KEYS.includes(pairedVibe as typeof VIBE_KEYS[number])
      ? pairedVibe
      : 'other';

    const current = interests[vibeKey] || [];
    if (current.includes(trimmed)) return;
    if (current.length >= MAX_INTERESTS_PER_VIBE || totalCount >= MAX_TOTAL_INTERESTS) return;

    const updated = { ...interests, [vibeKey]: [...current, trimmed] };
    setInterests(updated);
    await saveToDb(userId, updated);
  }, [interests, totalCount, saveToDb]);

  /** Get all active interests for selected vibes (for search enrichment) */
  const getActiveInterests = useCallback((selectedVibes: string[]): string[] => {
    if (!useInterests || !hasAny) return [];
    const result: string[] = [];
    for (const vibe of selectedVibes) {
      const vibeInterests = interests[vibe] || [];
      result.push(...vibeInterests);
    }
    // Always include 'other' interests
    const otherInterests = interests['other'] || [];
    result.push(...otherInterests);
    return [...new Set(result)];
  }, [interests, useInterests, hasAny]);

  return {
    interests,
    useInterests,
    hasAny,
    totalCount,
    loading,
    loadInterests,
    addInterest,
    removeInterest,
    toggleUseInterests,
    autoSaveInterest,
    getActiveInterests,
    clearInterests: useCallback(() => { setInterests({}); setUseInterests(false); }, []),
    MAX_INTERESTS_PER_VIBE,
    MAX_TOTAL_INTERESTS,
  };
}
